/**
 * Overview.jsx
 * Dynamic API Version
 */

import { useEffect, useState } from "react";
import { apiFetch } from "../helpers";

const FEATURES = [
  { icon: "🦷", title: "Pain-Free Treatments", desc: "Modern anaesthesia and gentle techniques ensure every visit is comfortable." },
  { icon: "🔬", title: "Advanced Technology", desc: "Digital X-rays, laser dentistry, and CAD/CAM same-day crowns." },
  { icon: "👨‍⚕️", title: "Experienced Doctors", desc: "Our team of specialists bring 15+ years of combined clinical expertise." },
  { icon: "🏥", title: "Hygienic Environment", desc: "Hospital-grade sterilisation and strict infection control protocols." },
  { icon: "📅", title: "Flexible Appointments", desc: "Morning, evening and weekend slots to fit your busy schedule." },
  { icon: "💳", title: "Affordable Pricing", desc: "Transparent pricing with EMI options and insurance accepted." },
];

export default function Overview() {
  const [data, setData] = useState({
    section_badge: "Why Choose Us",
    heading_line1: "Exceptional Dental Care",
    heading_highlight: "You Can Trust",
    description: "",
    about_title: "",
    about_description: "",

    stat1_value: "",
    stat1_label: "",

    stat2_value: "",
    stat2_label: "",

    stat3_value: "",
    stat3_label: "",

    stat4_value: "",
    stat4_label: "",

    badge1: "",
    badge2: "",
    badge3: "",
  });

  useEffect(() => {
    loadOverview();
  }, []);

  const loadOverview = async () => {
    try {
      const json = await apiFetch(
        `${process.env.REACT_APP_OVERVIEW_VIEW}`
      );

      if (json.status) {
        if (Array.isArray(json.data)) {
          setData(json.data[0] || {});
        } else {
          setData(json.data);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const stats = [
    [data.stat1_value, data.stat1_label],
    [data.stat2_value, data.stat2_label],
    [data.stat3_value, data.stat3_label],
    [data.stat4_value, data.stat4_label],
  ];

  const badges = [
    data.badge1,
    data.badge2,
    data.badge3,
  ].filter(Boolean);

  return (
    <section
      id="overview"
      style={{
        background: "#fff",
        padding: "90px 0 80px",
        fontFamily:
          "'Nunito','Segoe UI',sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: "0 1.5rem",
        }}
      >
        {/* Heading */}
        <div
          style={{
            textAlign: "center",
            marginBottom: 60,
          }}
        >
          <span
            style={{
              display: "inline-block",
              background: "#E1F5EE",
              color: "#085041",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "1.5px",
              textTransform:
                "uppercase",
              padding: "5px 16px",
              borderRadius: 20,
              marginBottom: 14,
            }}
          >
            {data.section_badge}
          </span>

          <h2
            style={{
              fontSize:
                "clamp(1.6rem,4vw,2.5rem)",
              fontWeight: 800,
              color: "#0A1628",
              margin:
                "0 0 16px",
            }}
          >
            {data.heading_line1}
            <br />
            <span
              style={{
                color: "#1D9E75",
              }}
            >
              {data.heading_highlight}
            </span>
          </h2>

          <p
            style={{
              color: "#6B7A8D",
              fontSize: 16,
              maxWidth: 520,
              margin: "0 auto",
              lineHeight: 1.7,
            }}
          >
            {data.description}
          </p>
        </div>

        {/* Main Grid */}
        <div
          className="overview-grid"
          style={{
            display: "grid",
            gridTemplateColumns:
              "1fr 1fr",
            gap: 56,
            alignItems:
              "center",
          }}
        >
          {/* Left */}
          <div>
            <div
              style={{
                display: "flex",
                gap: 16,
                marginBottom: 28,
              }}
            >
              <div
                style={{
                  width: 4,
                  borderRadius: 4,
                  background:
                    "linear-gradient(to bottom,#1D9E75,#378ADD)",
                }}
              />

              <div>
                <h3
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color:
                      "#0A1628",
                    margin:
                      "0 0 10px",
                  }}
                >
                  {data.about_title}
                </h3>

                <p
                  style={{
                    color:
                      "#6B7A8D",
                    fontSize: 15,
                    lineHeight: 1.8,
                    margin: 0,
                  }}
                >
                  {
                    data.about_description
                  }
                </p>
              </div>
            </div>

            {/* Stats */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "1fr 1fr",
                gap: 16,
                marginBottom: 28,
              }}
            >
              {stats.map(
                ([val, label], i) => (
                  <div
                    key={i}
                    style={{
                      background:
                        "#F8FAFC",
                      borderRadius: 12,
                      padding:
                        "16px 18px",
                      borderLeft:
                        "3px solid #1D9E75",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 800,
                        color:
                          "#0A1628",
                      }}
                    >
                      {val}
                    </div>

                    <div
                      style={{
                        fontSize: 12,
                        color:
                          "#6B7A8D",
                        marginTop: 3,
                      }}
                    >
                      {label}
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Badges */}
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap:
                  "wrap",
              }}
            >
              {badges.map(
                (b, i) => (
                  <span
                    key={i}
                    style={{
                      background:
                        "#E1F5EE",
                      color:
                        "#085041",
                      fontSize: 12,
                      fontWeight: 600,
                      padding:
                        "5px 12px",
                      borderRadius: 20,
                    }}
                  >
                    ✅ {b}
                  </span>
                )
              )}
            </div>
          </div>

          {/* Right Features */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "1fr 1fr",
              gap: 14,
            }}
          >
            {FEATURES.map(
              (f, i) => (
                <div
                  key={i}
                  style={{
                    background:
                      "#fff",
                    border:
                      "1px solid #E4EAF0",
                    borderRadius: 14,
                    padding:
                      "18px 16px",
                    transition:
                      ".25s",
                  }}
                >
                  <div
                    style={{
                      fontSize: 26,
                      marginBottom: 8,
                    }}
                  >
                    {f.icon}
                  </div>

                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color:
                        "#0A1628",
                      marginBottom: 5,
                    }}
                  >
                    {f.title}
                  </div>

                  <div
                    style={{
                      fontSize: 12,
                      color:
                        "#6B7A8D",
                      lineHeight: 1.6,
                    }}
                  >
                    {f.desc}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width:768px){
          .overview-grid{
            grid-template-columns:1fr!important;
            gap:36px!important;
          }
        }
      `}</style>
    </section>
  );
}